import { SearchBarProps } from "@omni/types/components/props/searchBar.props";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { setLayer } from "@omni/ui/utils/layer";
import { hasWindow } from "@omni/utils/dom.utils";
import { cn } from "@omni/utils/styles";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { Overlay } from "@/components/General/Overlay/overlay";
import SearchBoxComponent from "@/components/Search/SearchBar/AutocompleteSearchBox/SearchBox/searchBox.component";
import { ResultsPanel } from "@/components/Search/SearchBar/ResultsPanel/resultsPanel.container";
import { AutocompleteRef } from "@/components/Search/SearchBar/searchBar.types";
import { useAutocompleteApi } from "@/components/Search/SearchBar/useAutocompleteApi.hook";
import { getQuery } from "@/redux/modules/value/search/search.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { blurActiveElement } from "@/utils/blur";
import { KeyNames } from "@/utils/enums";
import { useScrollLock } from "@/utils/hooks/useScrollLock";
import { createKeyHandler } from "@/utils/keyboard";
import { isPageTemplate } from "@/utils/pageTemplate.utils";
import { parseUrl } from "@/utils/search/routing/configurationFromUrl";

const AUTOCOMPLETE_SEARCH_BOX_SCROLL_LOCK = "autocomplete-search-box";

export const SearchBar = ({ initialProducts }: SearchBarProps) => {
    const { t } = useGranite();

    const { lock, unlock } = useScrollLock(AUTOCOMPLETE_SEARCH_BOX_SCROLL_LOCK);

    const isSearchPage = isPageTemplate(PageTemplate.Search);
    const initialQuery =
        useSelector(getQuery) || (isSearchPage && hasWindow())
            ? parseUrl().q ||
              ((
                  document.getElementById(
                      "autocomplete-input"
                  ) as HTMLInputElement | null
              )?.value ??
                  "")
            : "";

    const autocompleteRef = useRef<AutocompleteRef>({
        form: null,
        input: null,
        query: initialQuery
    });
    const panelRef = useRef<HTMLDivElement>(null);

    const {
        autocomplete,
        isOpen,
        open,
        close,
        autocompleteActions,
        autocompleteState,
        items
    } = useAutocompleteApi({
        autocompleteRef: autocompleteRef,
        initialProducts: initialProducts
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        autocompleteRef.current.query = initialQuery;
        autocomplete.setQuery(initialQuery);
    }, [initialQuery]);

    const props = {
        root: autocomplete.getRootProps({
            "aria-labelledby": "submit-search-button"
        }),
        panel: autocomplete.getPanelProps({})
    };

    const handleOnClose = () => {
        close();
        blurActiveElement();
    };

    const handleOnKeyDown = createKeyHandler([
        {
            keys: [KeyNames.ESCAPE],
            handler: handleOnClose
        },
        {
            keys: [KeyNames.SPACE],
            handler: (event) => {
                if (isOpen) return;

                event.preventDefault();
                open();
            }
        },
        {
            defaultExcept: [KeyNames.TAB, KeyNames.SHIFT],
            handler: open
        }
    ]);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener(
            "keydown",
            (event) => {
                if (
                    (event.metaKey || event.ctrlKey) &&
                    event.key.toLowerCase() === KeyNames.K
                ) {
                    open();
                }
            },
            {
                signal: abortController.signal
            }
        );

        return () => {
            abortController.abort();
        };
    }, [open]);

    useEffect(() => {
        if (
            !autocompleteRef.current.form ||
            !autocompleteRef.current.input ||
            !panelRef.current
        ) {
            return;
        }

        const abortController = new AbortController();

        const { onTouchStart, onTouchMove, onMouseDown } =
            autocomplete.getEnvironmentProps({
                formElement: autocompleteRef.current.form,
                inputElement: autocompleteRef.current.input,
                panelElement: panelRef.current
            });

        window.addEventListener("mousedown", onMouseDown, {
            signal: abortController.signal
        });
        window.addEventListener("touchstart", onTouchStart, {
            signal: abortController.signal
        });
        window.addEventListener("touchmove", onTouchMove, {
            signal: abortController.signal
        });

        return () => {
            abortController.abort();
        };
    }, [autocomplete.getEnvironmentProps]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        (isOpen ? lock : unlock)();

        const navigationTopBarElement = document.querySelector(
            "section.navigation__top-bar"
        ) as HTMLElement | null;

        if (navigationTopBarElement) {
            setLayer(navigationTopBarElement, "navigation", !isOpen);
        }
    }, [isOpen]);

    return (
        <>
            <div
                {...props.root}
                className={cn("tw:group/autocomplete", {
                    "tw:bg-white tw:layer-modal/[2] tw:p-4 tw:fixed tw:w-full tw:h-full tw:top-0 tw:left-0":
                        isOpen,
                    "tw:lg:top-8.25 tw:lg:w-[calc(992px-4rem-80px)] tw:lg:h-auto tw:lg:rounded-lg tw:lg:left-[calc(50%-calc(992px-4rem-80px)/2)]":
                        isOpen,
                    "tw:xl:w-[calc(992px-1rem-80px)] tw:xl:left-[initial] tw:xl:-translate-x-4":
                        isOpen,
                    "tw:rounded-sm": !isOpen
                })}
                onClick={open}
                onKeyDown={handleOnKeyDown}
            >
                <SearchBoxComponent
                    ref={autocompleteRef}
                    isOpen={isOpen}
                    autocomplete={autocomplete}
                    autocompleteState={autocompleteState}
                    open={open}
                    close={handleOnClose}
                    autocompleteActions={autocompleteActions}
                />
                {isOpen && (
                    <>
                        <Button
                            type={"button"}
                            color={"light-gray"}
                            className={cn(
                                "tw:absolute tw:w-10 tw:h-10 tw:top-4 tw:-right-14 tw:bg-white tw:hidden",
                                "tw:lg:block"
                            )}
                            onClick={handleOnClose}
                            tabIndex={isOpen ? 0 : -1}
                            aria-label={t("accessibility.searchBox.close")}
                        >
                            <i className={cn("las la-times")} />
                        </Button>

                        <ResultsPanel
                            onMouseDown={props.panel.onMouseDown}
                            items={items}
                            autocompleteState={autocompleteState}
                            autocompleteActions={autocompleteActions}
                            autocompleteRef={autocompleteRef}
                        />
                    </>
                )}
            </div>
            <Overlay
                open={isOpen}
                onClose={handleOnClose}
                overlayHeader={true}
                scrollLock={false}
            >
                <Overlay.Content />
            </Overlay>
        </>
    );
};
